/**
 * useKioskFlexBandData Hook
 * 
 * Fetches FlexBand course enrollment data for the kiosk display.
 * Optimized for 100+ concurrent screens with:
 * - Cached Supabase client (shared across requests)
 * - 60-second polling interval
 * - Abort controller to cancel stale requests
 * - Minimal state updates (only when data changes)
 * 
 * @module kiosk/hooks/useKioskFlexBandData
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import {
    getKioskSupabaseClient,
    isSupabaseConfigured,
    getKioskToken,
    KIOSK_INTERVALS
} from '../utils/kioskUtils';

// ============================================================================
// Types
// ============================================================================

/**
 * Single FlexBand entry representing a student's course enrollment
 */
export interface FlexBandEntry {
    /** Student's first name */
    student_first_name: string;
    /** Student's class name (e.g., "3A") */
    class_name: string;
    /** Course name (e.g., "Kunst AG") */
    course_name: string;
    /** Subject icon emoji (nullable) */
    subject_icon: string | null;
    /** Room display name */
    room_name: string;
    /** Teacher names (comma-separated) */
    teacher_names: string;
    /** Room number for compact display */
    room_number?: string;
    /** ISO datetime string for lesson start */
    start_datetime: string;
    /** ISO datetime string for lesson end */
    end_datetime: string;
    /** School UUID */
    school_id: string;
    /** Whether the course lesson is cancelled */
    is_cancelled: boolean;
    /** Whether this student is a guest (trial enrollment) */
    is_guest: boolean;
    /** Guest course name if applicable */
    guest_course_name?: string;
    /** Guest course icon if applicable */
    guest_course_icon?: string;
    /** Dismissal info for cancelled courses */
    dismissal_info?: string;
    /** Group name for display organization */
    group_name?: string;
    /** Whether teacher was substituted (for 🔄 indicator) */
    is_teacher_substituted?: boolean;
    /** Whether room was changed (for red + 🔄 indicator) */
    is_room_substituted?: boolean;
}

/**
 * Hook return value
 */
interface UseKioskFlexBandDataReturn {
    /** Array of FlexBand entries */
    data: FlexBandEntry[];
    /** Whether initial load is in progress */
    loading: boolean;
    /** Error message if any */
    error: string | null;
    /** Timestamp of last successful data fetch */
    lastUpdated: Date;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Fetches and polls FlexBand data for the kiosk display.
 * 
 * @returns Object containing data, loading state, error, and last update time
 */
export const useKioskFlexBandData = (): UseKioskFlexBandDataReturn => {
    const [data, setData] = useState<FlexBandEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    // Abort controller for cancelling stale requests
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchData = useCallback(async () => {
        // Validate JWT exists
        if (!getKioskToken()) {
            setError('Kiosk session invalid (missing JWT)');
            setLoading(false);
            return;
        }

        // Validate Supabase configuration
        if (!isSupabaseConfigured()) {
            setError('Supabase configuration missing');
            setLoading(false);
            return;
        }

        // Get cached Supabase client
        const kioskClient = getKioskSupabaseClient();
        if (!kioskClient) {
            setError('Failed to initialize Supabase client');
            setLoading(false);
            return;
        }

        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            const { data: flexData, error: flexError } = await kioskClient
                .from('vw_kiosk_flexband_overview')
                .select('*')
                .order('class_name', { ascending: true })
                .order('student_first_name', { ascending: true });

            if (flexError) throw flexError;

            setData(flexData || []);
            setLastUpdated(new Date());
            setError(null);
        } catch (err: any) {
            // Ignore abort errors (expected when cancelling stale requests)
            if (err.name === 'AbortError') return;
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Initial fetch
        fetchData();

        // Set up polling interval
        const interval = setInterval(fetchData, KIOSK_INTERVALS.FLEXBAND_POLL);

        // Cleanup on unmount
        return () => {
            clearInterval(interval);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData]);

    return { data, loading, error, lastUpdated };
};
